import React, { ChangeEvent, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import ErrorMessage from 'components/ErrorMessage'

import { color } from 'styles/colors'

type CheckedState = 'indeterminate' | boolean

interface CheckboxProps {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  label: string | ReactNode
  listId?: string
  checked?: CheckedState
  className?: string
  errorMessage?: string
  disabled?: boolean
  isHtmlLabel?: boolean
  variant?: 'default' | 'dark'
}

const Checkbox = ({
  checked = false,
  onChange,
  label,
  listId = '',
  className = '',
  errorMessage = '',
  disabled = false,
  isHtmlLabel = false,
  variant = 'default'
}: CheckboxProps) => {
  const id = isHtmlLabel ? `checkbox_${listId}` : (label as string).replace(/\s+/g, '_') + listId
  const labelArray = isHtmlLabel ? [] : (label as string).split(/<\/?span>/)
  let labelContent: ReactNode

  if (isHtmlLabel) {
    labelContent = (
      <S.Label htmlFor={id} disabled={disabled}>
        {label}
      </S.Label>
    )
  } else {
    if (labelArray.length > 1) {
      labelContent = (
        <S.Label htmlFor={id} disabled={disabled}>
          {labelArray.map((labelPart, i) => {
            /* eslint-disable react/no-array-index-key */
            if (labelPart.includes(':-*/')) {
              return <S.FilteredLabel key={labelPart + id + i}>{labelPart.replace(':-*/', '')}</S.FilteredLabel>
            }
            return <React.Fragment key={labelPart + id + i}>{labelPart}</React.Fragment>
            /* eslint-enable react/no-array-index-key */
          })}
        </S.Label>
      )
    } else {
      labelContent = (
        <S.Label htmlFor={id} disabled={disabled}>
          {labelArray[0]}
        </S.Label>
      )
    }
  }

  return (
    <S.Checkbox {...{ className }}>
      <S.Input
        {...{ onChange, id, disabled, variant }}
        checked={checked === 'indeterminate' ? false : (checked as boolean)}
        ref={(input: HTMLInputElement | null) => {
          if (input) {
            input.indeterminate = checked === 'indeterminate'
          }
        }}
        type="checkbox"
        data-testid={id}
      />
      {labelContent}
      <S.ErrorMessage errorMessage={errorMessage} />
    </S.Checkbox>
  )
}

export default Checkbox

interface LabelProps {
  disabled?: boolean
}

interface InputProps {
  variant?: 'default' | 'dark'
}

const S = {
  Checkbox: styled.div`
    display: flex;
    justify-content: flex-start;
    font-weight: inherit;
    align-items: flex-start;
    margin-bottom: 7px;
  `,
  Input: styled.input.withConfig({
    shouldForwardProp: (prop) => !['variant'].includes(prop)
  })<InputProps>`
    margin-top: 3px;
    margin-right: 7px;
    cursor: pointer;
    accent-color: ${({ variant }) => variant === 'dark' ? color('termsCheckbox') : color('secondaryButtonBorder')};
  `,
  Label: styled.label<LabelProps>`
    ${({ disabled }) => disabled ? css`
      color: ${color('disabledText')};
      cursor: default;
    ` : ''}
    line-height: 1.3;
    cursor: pointer;
  `,
  FilteredLabel: styled.span`
    background-color: ${color('filteredText')};
    font-weight: 700;
  `,
  ErrorMessage: styled(ErrorMessage)`
    margin-left: auto;
    cursor: pointer;
  `
}
